import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import AssessmentService, {
  CreateAssessmentRequest,
  CreateAssessmentResponse,
  MetadataOption,
  ActivityScoreUnit,
  PrepareForVideoUploadRequest,
  RebaMetadataOptions,
  RulaMetadataOptions,
  PrepareForRulaVideoUploadRequest,
  ErgonetPrepareForVideoUploadRequest,
  ErgonetPrepareForRulaVideoUploadRequest,
} from "src/services/assessment/assessmentService";
import { Site } from "src/services/assessment/types/site";
import { AxiosResponse } from "axios";
import CsvToJsonService from "src/services/csvToJsonService";
import { ProcessPath } from "src/services/assessment/types/processPath";
import { SiteCsv } from "src/services/assessment/types/siteCsv";
import { ProcessPathToActivityCsv } from "src/services/assessment/types/processPathToActivityCsv";
import { SiteToProcessPathCsv } from "src/services/assessment/types/siteToProcessPathCsv";
import AssessmentVideoInfoService from "src/services/assessment/assessmentVideoInfoService";
import LocalAssessmentStoreService from "src/services/localAssessmentStore/localAssessmentStoreService";
import { isBeta, isLocal } from "src/utils/detectEnv";
import {
  CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT,
  TRA_ERRORS_MAPPINGS,
  TRA_TYPE_RULA,
} from "src/constants/constants";
import { CREATE_RULA_ADDITIONAL_INFO_INITIAL_STATE } from "src/services/assessment/constants";

export type CreateTraPayload = {
  owner: string;
  clientId: string;
  site: string;
  processPath: string;
  activity: string;
  type: string;
  siteType: string;
  organization: string;
  subOrganization: string;
  region: string;
  country: string;
};

export type RulaMetadata = {
  isLeftArmSupportedOrPersonLeaning: number;
  isLeftLowerArmAcrossMidlineOrOutToSide: number;
  isRightArmSupportedOrPersonLeaning: number;
  isRightLowerArmAcrossMidlineOrOutToSide: number;
  rightWristAwkwardPosition: number;
  leftWristAwkwardPosition: number;
  leftWristTwistPosition: number;
  rightWristTwistPosition: number;
  leftWristBentTwisted: number;
  rightWristBentTwisted: number;
  armMuscleUseSelection: string;
  armForceLoadSelection: string;
  isNeckTwisted: number;
  isTrunkTwisted: number;
  isLegFeetNotSupported: number;
  lowerBodyMuscleUseSelection: string;
  lowerBodyForceLoadSelection: string;
  comment: string;
};

export type createAssessmentState = {
  assessmentId: string; // used to check create or update assessment
  evaluationId: string; // used to check create or update evaluation
  evaluationError: keyof typeof TRA_ERRORS_MAPPINGS; // used to control error message display
  pageProgress: number;
  sites: Site[];
  selectedSite: Site;
  selectedActivity: string;
  customActivity: string;
  selectedProcessPath: ProcessPath;
  videoUrl: string;
  videoFileName: string;
  selectedAssessment: string;
  forceLoadOptions: MetadataOption[];
  couplingOptions: MetadataOption[];
  activityScoreOptions: MetadataOption[];
  activityScoreUnits: ActivityScoreUnit[];
  forceLoadSelection: string;
  isShockOrRapidBuildUpForce: boolean;
  couplingSelection: string;
  activitySelections: string[];
  activityIndex: number;
  repeatedSmallChecked: boolean;
  actionCausesChecked: boolean;
  leftWristAwkwardPosition: number;
  leftWristBentTwisted: boolean;
  isRightArmSupportedOrPersonLeaning: boolean;
  isLeftArmSupportedOrPersonLeaning: boolean;
  isTrunkTwisted: boolean;
  isNeckTwisted: boolean;
  rightWristAwkwardPosition: number;
  rightWristBentTwisted: boolean;
  comment: string;
  isCommentsValid: boolean;
  assessment: CreateAssessmentResponse;
  isFulfilled: boolean;
  isLoading: boolean;
  isError: boolean;
  isUploadVideo: boolean;
  videoUploadQueue: string[];
  rulaMetadata: RulaMetadata;
  rulaMetadataOptions: RulaMetadataOptions;
  austinQueryParams: Record<string, string>;
};

const initialState: createAssessmentState = {
  assessmentId: "",
  evaluationId: "",
  evaluationError: "NONE",
  pageProgress: 0,
  sites: [],
  selectedSite: {
    name: "",
    processPaths: [],
    organization: "",
    subOrganization: "",
    region: "",
    siteType: "",
    country: "",
  },
  customActivity: "",
  selectedActivity: "",
  selectedProcessPath: {
    name: "",
    activities: [],
  },
  videoUrl: "",
  videoFileName: "",
  selectedAssessment: "",
  couplingOptions: [],
  activityScoreOptions: [],
  activityScoreUnits: [],
  forceLoadOptions: [],
  forceLoadSelection: "",
  isShockOrRapidBuildUpForce: false,
  couplingSelection: "",
  activitySelections: [],
  activityIndex: -1,
  repeatedSmallChecked: false,
  actionCausesChecked: false,
  leftWristAwkwardPosition: -1,
  leftWristBentTwisted: false,
  isRightArmSupportedOrPersonLeaning: false,
  isLeftArmSupportedOrPersonLeaning: false,
  isTrunkTwisted: false,
  isNeckTwisted: false,
  rightWristAwkwardPosition: -1,
  rightWristBentTwisted: false,
  comment: "",
  isCommentsValid: true,
  assessment: {
    assessmentId: "",
    followupEvaluationId: "",
    initialEvaluationId: "",
  },
  isFulfilled: false,
  isLoading: false,
  isError: false,
  isUploadVideo: true,
  videoUploadQueue: [],
  rulaMetadata: CREATE_RULA_ADDITIONAL_INFO_INITIAL_STATE,
  rulaMetadataOptions: { forceLoadOptions: [], muscleUseOptions: [] },
  austinQueryParams: {},
};

const generateCreateAssessmentPayload = (
  state: createAssessmentState,
  clientId: string,
) => {
  return {
    owner: clientId,
    clientId: "HFE",
    site: state.selectedSite.name,
    processPath: state.selectedProcessPath.name,
    activity: state.selectedActivity,
    type: state.selectedAssessment,
    siteType: state.selectedSite.siteType,
    organization: state.selectedSite.organization,
    subOrganization: state.selectedSite.subOrganization,
    region: state.selectedSite.region,
    country: state.selectedSite.country,
  };
};

const generateActivityScoreUnits = (
  activityScoreOptions: MetadataOption[],
): ActivityScoreUnit[] => {
  return activityScoreOptions.map((activityScoreOption) => {
    const activityScoreUnit: ActivityScoreUnit = {
      ...activityScoreOption,
      isSelected: false,
    };
    return activityScoreUnit;
  });
};

const generateReplaceEvaluationIdPayload = (
  state: createAssessmentState,
  clientId: string,
  evaluationId: string,
) => {
  return {
    owner: clientId,
    clientId: "HFE",
    evaluationIdToReplace: evaluationId,
    assessmentId: state.assessmentId,
  };
};

export const createNewAssessment = createAsyncThunk(
  "assessment/createAssessment",
  async (clientId: string, { getState }) => {
    const state = (await (getState() as any)
      .createAssessment) as createAssessmentState;
    const { data } = await AssessmentService.createAssessment(
      generateCreateAssessmentPayload(state, clientId),
    );
    return data;
  },
);

export const getAllSites = createAsyncThunk(
  "assessment/getAllSites",
  async () => {
    const region: string = "us-east-1";
    let sitesDataFileSrc, sitesToProcessPathsDataFileSrc;
    let processPathsToActivitiesDataFileSrc =
      require("src/assets/austin_process_path_to_activities.csv").default;
    switch (region) {
      case "eu-west-1": {
        sitesDataFileSrc = require("src/assets/austin_sites_eu.csv").default;
        sitesToProcessPathsDataFileSrc =
          require("src/assets/austin_site_to_process_paths_eu.csv").default;
        break;
      }
      case "ap-northeast-1": {
        sitesDataFileSrc = require("src/assets/austin_sites_fe.csv").default;
        sitesToProcessPathsDataFileSrc =
          require("src/assets/austin_site_to_process_paths_fe.csv").default;
        break;
      }
      default: {
        sitesDataFileSrc = require("src/assets/austin_sites_na.csv").default;
        sitesToProcessPathsDataFileSrc =
          require("src/assets/austin_site_to_process_paths_na.csv").default;
      }
    }

    const sites: SiteCsv[] =
      await CsvToJsonService.fetchCsvAndConvertToJson(sitesDataFileSrc);
    const siteToProcessPaths: SiteToProcessPathCsv[] =
      await CsvToJsonService.fetchCsvAndConvertToJson(
        sitesToProcessPathsDataFileSrc,
      );
    const processPathToActivities: ProcessPathToActivityCsv[] =
      await CsvToJsonService.fetchCsvAndConvertToJson(
        processPathsToActivitiesDataFileSrc,
      );
    return { sites, siteToProcessPaths, processPathToActivities };
  },
);

export const getRebaMetadataOptions = createAsyncThunk(
  "assessment/getMetadataOptions",
  async () => {
    const payload = {
      metadataType: "REBA",
    };
    const { data } = await AssessmentService.getMetadataOptions(payload);
    // Parse the nested JSON string
    const parsedData: RebaMetadataOptions = JSON.parse(data.metadata);

    return parsedData;
  },
);
export const getRulaMetadataOptions = createAsyncThunk(
  "assessment/getRulaMetadataOptions",
  async () => {
    const payload = {
      metadataType: "RULA",
    };
    const { data } = await AssessmentService.getMetadataOptions(payload);
    // Parse the nested JSON string
    const parsedData: RulaMetadataOptions = JSON.parse(data.metadata);

    return parsedData;
  },
);

const getActivitySelections = (
  activityScoreUnits: ActivityScoreUnit[],
): string[] =>
  activityScoreUnits.filter((item) => item.isSelected).map((item) => item.id);

const generateErgonetPrepareForVideoUploadPayload = (
  state: createAssessmentState,
  clientId: string,
) => {
  return {
    owner: clientId,
    clientId: "HFE",
    assessmentType: state.selectedAssessment,
    evaluationId: state.assessment.initialEvaluationId,
    forceLoadSelection: state.forceLoadSelection,
    couplingSelection: state.couplingSelection,
    activitySelections: state.activitySelections,
    isShockOrRapidBuildUpForce: state.isShockOrRapidBuildUpForce ? 1 : 0,
    isRightArmSupportedOrPersonLeaning: Number(
      state.isRightArmSupportedOrPersonLeaning,
    ),
    isLeftArmSupportedOrPersonLeaning: Number(
      state.isLeftArmSupportedOrPersonLeaning,
    ),
    comment: state.comment,
  };
};

const generateErgonetRulaPrepareForVideoUploadPayload = (
  state: createAssessmentState,
  clientId: string,
) => {
  return {
    owner: clientId,
    clientId: "HFE",
    assessmentType: state.selectedAssessment,
    evaluationId: state.assessment.initialEvaluationId,
    isLeftArmSupportedOrPersonLeaning:
      state.rulaMetadata.isLeftArmSupportedOrPersonLeaning,
    isRightArmSupportedOrPersonLeaning:
      state.rulaMetadata.isRightArmSupportedOrPersonLeaning,
    armMuscleUseSelection: state.rulaMetadata.armMuscleUseSelection,
    armForceLoadSelection: state.rulaMetadata.armForceLoadSelection,
    isLegFeetNotSupported: state.rulaMetadata.isLegFeetNotSupported,
    lowerBodyMuscleUseSelection: state.rulaMetadata.lowerBodyMuscleUseSelection,
    lowerBodyForceLoadSelection: state.rulaMetadata.lowerBodyForceLoadSelection,
    comment: state.rulaMetadata.comment,
  };
};

export const recreateAssessmentFromLocal = createAsyncThunk(
  "assessment/recreateAssessmentFromLocal",
  async (
    payload: {
      assessmentPayload: CreateAssessmentRequest;
      evaluationId: null | undefined | string;
      prepareForVideoUploadPayload:
        | PrepareForVideoUploadRequest
        | PrepareForRulaVideoUploadRequest
        | ErgonetPrepareForVideoUploadRequest
        | ErgonetPrepareForRulaVideoUploadRequest;
      videoBlob: Blob;
    },
    { getState },
  ) => {
    let evaluationId = payload.evaluationId;
    if (evaluationId === null) {
      evaluationId = await AssessmentService.createAssessment(
        payload.assessmentPayload,
      )
        .then((response: any) => {
          return response.data!.initialEvaluationId;
        })
        .catch(() => {
          return null;
        });

      if (evaluationId === null || evaluationId === undefined) {
        console.log("Could not create assessment! Try again later");
        return;
      }
    }
    const preparePayload = {
      ...payload.prepareForVideoUploadPayload,
      evaluationId: evaluationId!,
    };
    const prepareApi =
      payload.assessmentPayload.type === TRA_TYPE_RULA
        ? AssessmentService.prepareRulaForVideoUpload(preparePayload as any)
        : AssessmentService.prepareForVideoUpload(preparePayload as any);
    await prepareApi
      .then(async (response: AxiosResponse) => {
        const file = new File([payload.videoBlob], "video", {
          type: payload.videoBlob.type,
        });
        await AssessmentService.uploadVideoToErgoNet(
          response.data.presignedUrlForErgoNetUpload,
          file,
        );
      })
      .catch(() => {
        console.log("Assessment created but video could not be uploaded");
      });
  },
);

export const executeUploadVideoWorkflow = createAsyncThunk(
  "assessment/createNewAssessmentAndUploadVideoWorkflow",
  async (
    payload: {
      clientId: string;
      traCreatePayload?: CreateTraPayload;
      evaluationId?: string;
    },
    { getState },
  ) => {
    const state = (await (getState() as any)
      .createAssessment) as createAssessmentState;
    let evaluationId: string = "";
    const assessmentPayload = generateCreateAssessmentPayload(
      state,
      payload.clientId,
    );
    const prepareForVideoUploadPayload =
      state.selectedAssessment === TRA_TYPE_RULA
        ? generateErgonetRulaPrepareForVideoUploadPayload(
            state,
            payload.clientId,
          )
        : generateErgonetPrepareForVideoUploadPayload(state, payload.clientId);
    if (!payload.evaluationId) {
      evaluationId = await AssessmentService.createAssessment(
        payload.traCreatePayload || assessmentPayload,
      )
        .then((response: any) => {
          return response.data!.initialEvaluationId;
        })
        .catch(() => {
          return null;
        });

      if (evaluationId === null || evaluationId === undefined) {
        // assessment not created and video not uploaded
        saveAssessmentLocally(
          state.videoUrl,
          evaluationId,
          assessmentPayload,
          prepareForVideoUploadPayload,
        );
        return;
      }
    } else {
      evaluationId = payload.evaluationId;
    }
    state.selectedAssessment === TRA_TYPE_RULA
      ? await AssessmentService.prepareRulaForVideoUpload({
          ...generateErgonetRulaPrepareForVideoUploadPayload(
            state,
            payload.clientId,
          ),
          evaluationId: evaluationId,
        })
          .then(async (response: AxiosResponse) => {
            const video: Blob = await fetch(state.videoUrl).then((r) =>
              r.blob(),
            );
            const fileType = state.videoFileName.endsWith("mp4")
              ? "video/mp4"
              : "video/quicktime";
            const file = new File([video], state.videoFileName, {
              type: fileType,
            });
            await AssessmentService.uploadVideoToErgoNet(
              response.data.presignedUrlForErgoNetUpload,
              file,
            );
            URL.revokeObjectURL(state.videoUrl);
          })
          .catch(() => {
            saveAssessmentLocally(
              state.videoUrl,
              evaluationId,
              assessmentPayload,
              prepareForVideoUploadPayload,
            );
          })
      : await AssessmentService.prepareForVideoUpload({
          ...generateErgonetPrepareForVideoUploadPayload(
            state,
            payload.clientId,
          ),
          evaluationId: evaluationId,
        })
          .then(async (response: AxiosResponse) => {
            const video: Blob = await fetch(state.videoUrl).then((r) =>
              r.blob(),
            );
            const fileType = state.videoFileName.endsWith("mp4")
              ? "video/mp4"
              : "video/quicktime";
            const file = new File([video], state.videoFileName, {
              type: fileType,
            });
            await AssessmentService.uploadVideoToErgoNet(
              response.data.presignedUrlForErgoNetUpload,
              file,
            );
            URL.revokeObjectURL(state.videoUrl);
          })
          .catch(() => {
            saveAssessmentLocally(
              state.videoUrl,
              evaluationId,
              assessmentPayload,
              prepareForVideoUploadPayload,
            );
          });
  },
);

const saveAssessmentLocally = async (
  videoUrl: string,
  evaluationId: null | undefined | string,
  createAssessmentPayload: CreateAssessmentRequest,
  prepareForVideoUploadPayload:
    | PrepareForVideoUploadRequest
    | PrepareForRulaVideoUploadRequest
    | ErgonetPrepareForVideoUploadRequest
    | ErgonetPrepareForRulaVideoUploadRequest,
) => {
  if (!isBeta() && !isLocal()) {
    return;
  }
  const video: Blob = await fetch(videoUrl).then((r) => r.blob());
  LocalAssessmentStoreService.addEntryToStore(
    video,
    new Date().getTime(),
    evaluationId,
    createAssessmentPayload,
    prepareForVideoUploadPayload,
  );
};

export const executeReUploadVideoWorkflow = createAsyncThunk(
  "assessment/reUploadVideoWorkflow",
  async (payload: { clientId: string; evaluationId: string }, { getState }) => {
    const state = (await (getState() as any)
      .createAssessment) as createAssessmentState;
    await AssessmentService.replaceEvaluationInAssessment(
      generateReplaceEvaluationIdPayload(
        state,
        payload.clientId,
        payload.evaluationId,
      ),
    ).then(async (response: AxiosResponse) => {
      const evaluationId = response.data.evaluationId;
      const handleUpload = async (response: AxiosResponse) => {
        const video: Blob = await fetch(state.videoUrl).then((r) => r.blob());
        const fileType = state.videoFileName.endsWith("mp4")
          ? "video/mp4"
          : "video/quicktime";
        const file = new File([video], state.videoFileName, { type: fileType });
        await AssessmentService.uploadVideoToErgoNet(
          response.data.presignedUrlForErgoNetUpload,
          file,
        );
        URL.revokeObjectURL(state.videoUrl);
      };
      if (state.selectedAssessment === TRA_TYPE_RULA) {
        await AssessmentService.prepareRulaForVideoUpload({
          ...generateErgonetRulaPrepareForVideoUploadPayload(
            state,
            payload.clientId,
          ),
          evaluationId,
        }).then(handleUpload);
      } else {
        await AssessmentService.prepareForVideoUpload({
          ...generateErgonetPrepareForVideoUploadPayload(
            state,
            payload.clientId,
          ),
          evaluationId,
        }).then(handleUpload);
      }
    });
  },
);

const { reducer, actions } = createSlice({
  name: "createAssessmentSlice",
  initialState,
  reducers: {
    setSite: (state, action) => {
      const site = state.sites.find((site) => site.name === action.payload);
      if (site !== undefined) {
        state.selectedSite = site;
      }
      state.selectedProcessPath = { name: "", activities: [] };
      state.selectedActivity = "";
      state.customActivity = "";
    },
    setProcessPath: (state, action) => {
      const processPath = state.selectedSite.processPaths.find(
        (processPath) => processPath.name === action.payload,
      );
      if (processPath !== undefined) {
        state.selectedProcessPath = processPath;
      }
      if (!state.austinQueryParams.activity) {
        state.selectedActivity = "";
        state.customActivity = "";
      }
    },
    setActivity: (state, action) => {
      state.selectedActivity = action.payload;
    },
    setCustomActivity: (state, action) => {
      state.customActivity = action.payload;
    },
    setVideoFile: (state, action) => {
      state.videoFileName = action.payload[0].path;
      state.videoUrl = URL.createObjectURL(new Blob(action.payload));
    },
    setVideoUrl: (state, action) => {
      state.videoUrl = action.payload;
    },
    setCustomActivityAsSelectedActivity: (state, action) => {
      state.selectedActivity = state.customActivity;
    },
    setAssessmentType: (state, action) => {
      state.selectedAssessment = action.payload;
    },
    setPageProgress: (state, action) => {
      state.pageProgress = action.payload;
    },
    setForceLoadSelection: (state, action) => {
      state.forceLoadSelection = action.payload;
    },
    setCouplingSelection: (state, action) => {
      state.couplingSelection = action.payload;
    },
    setRapidBuildForce: (state, action) => {
      state.isShockOrRapidBuildUpForce = action.payload;
    },
    setActivityIndex: (state, action) => {
      state.activityIndex = action.payload;
    },
    setLeftWristOption: (state, action) => {
      state.leftWristAwkwardPosition = action.payload;
    },
    setLeftWristOptionAddition: (state, action) => {
      state.leftWristBentTwisted = action.payload;
    },
    setIsRightArmSupportedOrPersonLeaning: (state, action) => {
      state.isRightArmSupportedOrPersonLeaning = action.payload;
    },
    setIsLeftArmSupportedOrPersonLeaning: (state, action) => {
      state.isLeftArmSupportedOrPersonLeaning = action.payload;
    },
    setIsTrunkTwisted: (state, action) => {
      state.isTrunkTwisted = action.payload;
    },
    setIsNeckTwisted: (state, action) => {
      state.isNeckTwisted = action.payload;
    },
    setRightWristOption: (state, action) => {
      state.rightWristAwkwardPosition = action.payload;
    },
    setRightWristOptionAddition: (state, action) => {
      state.rightWristBentTwisted = action.payload;
    },
    setAdditionalComments: (state, action) => {
      state.comment = action.payload;
      state.isCommentsValid =
        state.comment.length <= CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT;
    },
    setIsUploadVideo: (state, action) => {
      state.isUploadVideo = action.payload;
    },
    resetCreateAssessment: (state) => {
      return {
        ...initialState,
        videoUploadQueue: state.videoUploadQueue,
        assessmentId: state.assessmentId,
      };
    },
    pushVideoUploadQueue: (state, action) => {
      state.videoUploadQueue.push(action.payload);
    },
    popVideoUploadQueue: (state, action) => {
      state.videoUploadQueue = state.videoUploadQueue.filter(
        (e) => e !== action.payload,
      );
    },
    setAssessmentId: (state, action) => {
      state.assessmentId = action.payload;
    },
    setEvaluationId: (state, action) => {
      state.evaluationId = action.payload;
    },
    setEvaluationError: (state, action) => {
      state.evaluationError = action.payload;
    },
    setIsSelected: (state, action) => {
      const { id, isSelected } = action.payload;
      const activityScoreUnit = state.activityScoreUnits.find(
        (unit) => unit.id === id,
      );
      if (activityScoreUnit) {
        activityScoreUnit.isSelected = isSelected;
      }
      state.activitySelections = getActivitySelections(
        state.activityScoreUnits,
      );
    },
    updateRulaMetadata: <K extends keyof RulaMetadata>(
      state: createAssessmentState,
      action: PayloadAction<{ metadeta: K; value: RulaMetadata[K] }>,
    ) => {
      state.rulaMetadata[action.payload.metadeta] = action.payload.value;
    },
    setAustinQueryParams: (state, { payload }) => {
      state.austinQueryParams = payload;
    },
    setActivitySelections: (state, { payload }) => {
      state.activitySelections = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      executeUploadVideoWorkflow.pending,
      (state, { payload }) => {
        state.isLoading = true;
      },
    );
    builder.addCase(
      executeUploadVideoWorkflow.fulfilled,
      (state, { payload }) => {
        state.isFulfilled = true;
        state.isLoading = false;
      },
    );
    builder.addCase(
      executeUploadVideoWorkflow.rejected,
      (state, { payload }) => {
        state.isError = true;
        state.isLoading = false;
      },
    );
    builder.addCase(
      executeReUploadVideoWorkflow.pending,
      (state, { payload }) => {
        state.isLoading = true;
      },
    );
    builder.addCase(
      executeReUploadVideoWorkflow.fulfilled,
      (state, { payload }) => {
        state.isFulfilled = true;
        state.isLoading = false;
      },
    );
    builder.addCase(
      executeReUploadVideoWorkflow.rejected,
      (state, { payload }) => {
        state.isError = true;
        state.isLoading = false;
      },
    );
    builder.addCase(getAllSites.fulfilled, (state, { payload }) => {
      state.sites = AssessmentVideoInfoService.constructSiteData(
        payload.processPathToActivities,
        payload.siteToProcessPaths,
        payload.sites,
      );
    });
    builder.addCase(getRebaMetadataOptions.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.activityScoreUnits = generateActivityScoreUnits(
        payload.activityScoreOptions,
      );
      state.couplingOptions = payload.couplingOptions;
      state.forceLoadOptions = payload.forceLoadOptions;
    });
    builder.addCase(getRulaMetadataOptions.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.rulaMetadataOptions = payload;
    });
  },
});

export const {
  setSite,
  setProcessPath,
  setActivity,
  setCustomActivity,
  setCustomActivityAsSelectedActivity,
  setVideoFile,
  setPageProgress,
  setAssessmentType,
  setForceLoadSelection,
  setRapidBuildForce,
  setCouplingSelection,
  setActivityIndex,
  setLeftWristOption,
  setLeftWristOptionAddition,
  setIsRightArmSupportedOrPersonLeaning,
  setIsLeftArmSupportedOrPersonLeaning,
  setIsTrunkTwisted,
  setIsNeckTwisted,
  setRightWristOption,
  setRightWristOptionAddition,
  setAdditionalComments,
  setIsUploadVideo,
  resetCreateAssessment,
  setVideoUrl,
  pushVideoUploadQueue,
  popVideoUploadQueue,
  setAssessmentId,
  setEvaluationId,
  setEvaluationError,
  setIsSelected,
  updateRulaMetadata,
  setAustinQueryParams,
  setActivitySelections,
} = actions;

export default reducer;
